import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  TrendingUp, Users, Clock, Mail, AlertTriangle, Target,
  Calendar, BarChart3, PieChart as PieChartIcon, Activity, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { automatizacionAPI } from '../api';
import IAFeedbackPanel from './IAFeedbackPanel';

const COLORES_CHART = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#6366f1', '#ec4899', '#14b8a6', '#6b7280'];

function KpiCard({ title, value, subtitle, icon: Icon, color }) {
  return (
    <Card data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
            <p className={`text-2xl font-bold mt-1 ${color || 'text-gray-900'}`}>{value}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
          <div className={`p-2.5 rounded-lg ${color ? 'bg-opacity-10' : 'bg-gray-50'}`}
               style={{ backgroundColor: color ? `${color}15` : undefined }}>
            <Icon className="h-5 w-5" style={{ color: color || '#6b7280' }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function formatMinutes(mins) {
  if (!mins || mins === 0) return '0 min';
  if (mins < 60) return `${Math.round(mins)} min`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  if (h < 24) return `${h}h ${m}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

export default function EstadisticasAutomatizacion() {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [procuradores, setProcuradores] = useState([]);
  const [volumen, setVolumen] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [horasPunta, setHorasPunta] = useState([]);

  const [periodoVolumen, setPeriodoVolumen] = useState('mes');
  const [periodoUsuario, setPeriodoUsuario] = useState('mes');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [kpiRes, procRes, volRes, tipoRes, usrRes, hpRes] = await Promise.all([
        automatizacionAPI.getEstadisticasKpis(),
        automatizacionAPI.getEstadisticasProcurador(),
        automatizacionAPI.getEstadisticasVolumen(periodoVolumen),
        automatizacionAPI.getEstadisticasTipoTramite(),
        automatizacionAPI.getEstadisticasUsuario(periodoUsuario),
        automatizacionAPI.getEstadisticasHorasPunta(),
      ]);
      setKpis(kpiRes.data);
      setProcuradores(procRes.data.procuradores || []);
      setVolumen(volRes.data.datos || []);
      setTipos(tipoRes.data.tipos || []);
      setUsuarios(usrRes.data.usuarios || []);
      setHorasPunta(hpRes.data.horas_punta || []);
    } catch (err) {
      console.error('Error cargando estadísticas:', err);
    } finally {
      setLoading(false);
    }
  }, [periodoVolumen, periodoUsuario]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Reload volume when period changes
  const handlePeriodoVolumen = async (p) => {
    setPeriodoVolumen(p);
    try {
      const res = await automatizacionAPI.getEstadisticasVolumen(p);
      setVolumen(res.data.datos || []);
    } catch (e) { console.error(e); }
  };
  const handlePeriodoUsuario = async (p) => {
    setPeriodoUsuario(p);
    try {
      const res = await automatizacionAPI.getEstadisticasUsuario(p);
      setUsuarios(res.data.usuarios || []);
    } catch (e) { console.error(e); }
  };

  // Build heatmap grid
  const heatmapGrid = [];
  const dias = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  const diaMap = { 2: 0, 3: 1, 4: 2, 5: 3, 6: 4, 7: 5, 1: 6 }; // MySQL DAYOFWEEK → index
  const maxHp = Math.max(1, ...horasPunta.map(h => h.total));
  for (let d = 0; d < 7; d++) {
    for (let h = 7; h <= 20; h++) {
      const match = horasPunta.find(hp => diaMap[hp.dia_num] === d && hp.hora === h);
      heatmapGrid.push({ dia: d, hora: h, total: match ? match.total : 0 });
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="stats-loading">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        <span className="ml-3 text-gray-500">Cargando estadísticas...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="estadisticas-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <KpiCard title="Total emails" value={kpis?.total_emails || 0} icon={Mail} color="#3b82f6" />
        <KpiCard title="Confirmados" value={kpis?.confirmados || 0} icon={TrendingUp} color="#10b981" />
        <KpiCard title="Pendientes" value={kpis?.pendientes || 0} icon={Clock} color="#f59e0b" />
        <KpiCard title="Urgentes" value={kpis?.urgentes_pendientes || 0} icon={AlertTriangle} color="#ef4444" />
        <KpiCard title="Tasa confirm." value={`${kpis?.tasa_confirmacion || 0}%`} icon={Target} color="#8b5cf6" />
        <KpiCard title="Tiempo medio" value={formatMinutes(kpis?.tiempo_medio_minutos)} icon={Clock} color="#6366f1" />
        <KpiCard title="Precisión IA" value={`${kpis?.precision_ia || 0}%`} icon={Activity} color="#14b8a6" />
      </div>

      {/* Row 1: Volumen temporal + Tipo trámite */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Volumen temporal - 2/3 */}
        <Card className="lg:col-span-2" data-testid="chart-volumen">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-blue-600" />
                Volumen de emails
              </CardTitle>
              <Select value={periodoVolumen} onValueChange={handlePeriodoVolumen}>
                <SelectTrigger className="w-28 h-8 text-xs" data-testid="periodo-volumen">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hoy">Hoy</SelectItem>
                  <SelectItem value="semana">Semana</SelectItem>
                  <SelectItem value="mes">Mes</SelectItem>
                  <SelectItem value="año">Año</SelectItem>
                  <SelectItem value="todo">Todo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {volumen.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={volumen} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="confirmados" name="Confirmados" fill="#10b981" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="pendientes" name="Pendientes" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-gray-400 py-10 text-sm">Sin datos para este período</p>
            )}
          </CardContent>
        </Card>

        {/* Tipo trámite - 1/3 */}
        <Card data-testid="chart-tipos">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <PieChartIcon className="h-4 w-4 text-purple-600" />
              Por tipo de trámite
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tipos.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={tipos}
                      dataKey="total"
                      nameKey="label"
                      cx="50%" cy="50%"
                      innerRadius={40} outerRadius={70}
                      paddingAngle={2}
                    >
                      {tipos.map((t, i) => (
                        <Cell key={i} fill={t.color || COLORES_CHART[i % COLORES_CHART.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, name) => [`${v} emails`, name]} contentStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1 mt-2">
                  {tipos.map((t, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.color || COLORES_CHART[i] }} />
                        <span className="text-gray-600">{t.label}</span>
                      </div>
                      <span className="font-medium">{t.total}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-center text-gray-400 py-10 text-sm">Sin datos</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Procuradores + Usuarios */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Por procurador */}
        <Card data-testid="chart-procuradores">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Users className="h-4 w-4 text-indigo-600" />
              Emails por procurador
            </CardTitle>
          </CardHeader>
          <CardContent>
            {procuradores.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(200, procuradores.length * 36)}>
                <BarChart data={procuradores.slice(0, 15)} layout="vertical" margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis
                    dataKey="procurador" type="category" width={140}
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) => v.length > 22 ? v.substring(0, 20) + '...' : v}
                  />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="confirmados" name="Confirmados" fill="#10b981" stackId="a" radius={[0, 3, 3, 0]} />
                  <Bar dataKey="pendientes" name="Pendientes" fill="#f59e0b" stackId="a" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-gray-400 py-10 text-sm">Sin datos</p>
            )}
          </CardContent>
        </Card>

        {/* Por usuario */}
        <Card data-testid="chart-usuarios">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Users className="h-4 w-4 text-green-600" />
                Productividad por usuario
              </CardTitle>
              <Select value={periodoUsuario} onValueChange={handlePeriodoUsuario}>
                <SelectTrigger className="w-28 h-8 text-xs" data-testid="periodo-usuario">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="semana">Semana</SelectItem>
                  <SelectItem value="mes">Mes</SelectItem>
                  <SelectItem value="año">Año</SelectItem>
                  <SelectItem value="todo">Todo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {usuarios.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={usuarios} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="usuario" tick={{ fontSize: 10 }}
                           tickFormatter={(v) => v.length > 12 ? v.substring(0, 10) + '...' : v} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Bar dataKey="total_confirmados" name="Confirmados" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="urgentes_tramitados" name="Urgentes" fill="#ef4444" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                {/* Table below */}
                <div className="mt-3 border rounded overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-1.5 font-medium text-gray-600">Usuario</th>
                        <th className="text-center px-2 py-1.5 font-medium text-gray-600">Confirm.</th>
                        <th className="text-center px-2 py-1.5 font-medium text-gray-600">Urgentes</th>
                        <th className="text-center px-2 py-1.5 font-medium text-gray-600">T. medio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usuarios.map((u, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-3 py-1.5 font-medium">{u.usuario}</td>
                          <td className="text-center px-2 py-1.5">{u.total_confirmados}</td>
                          <td className="text-center px-2 py-1.5">
                            {u.urgentes_tramitados > 0 && (
                              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">{u.urgentes_tramitados}</Badge>
                            )}
                            {u.urgentes_tramitados === 0 && '0'}
                          </td>
                          <td className="text-center px-2 py-1.5 text-gray-500">{formatMinutes(u.tiempo_medio_min)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p className="text-center text-gray-400 py-10 text-sm">Sin datos para este período</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Horas punta (heat map) */}
      <Card data-testid="chart-horas-punta">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Calendar className="h-4 w-4 text-amber-600" />
            Mapa de actividad — Emails recibidos por día y hora
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <div className="min-w-[600px]">
              {/* Hour headers */}
              <div className="flex items-center gap-0.5 mb-1 ml-10">
                {Array.from({ length: 14 }, (_, i) => i + 7).map(h => (
                  <div key={h} className="flex-1 text-center text-[10px] text-gray-400">{h}h</div>
                ))}
              </div>
              {/* Grid rows */}
              {dias.map((dia, d) => (
                <div key={d} className="flex items-center gap-0.5 mb-0.5">
                  <div className="w-10 text-xs text-gray-500 text-right pr-2 font-medium">{dia}</div>
                  {Array.from({ length: 14 }, (_, i) => i + 7).map(h => {
                    const cell = heatmapGrid.find(c => c.dia === d && c.hora === h);
                    const val = cell ? cell.total : 0;
                    const intensity = val > 0 ? Math.min(0.15 + (val / maxHp) * 0.85, 1) : 0;
                    return (
                      <div
                        key={h}
                        className="flex-1 aspect-square rounded-sm flex items-center justify-center text-[9px] font-medium cursor-default transition-colors"
                        style={{
                          backgroundColor: val > 0 ? `rgba(139, 92, 246, ${intensity})` : '#f3f4f6',
                          color: intensity > 0.5 ? '#fff' : '#6b7280',
                          minHeight: '28px',
                        }}
                        title={`${dia} ${h}:00 — ${val} emails`}
                      >
                        {val > 0 ? val : ''}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* IA Feedback / Aprendizaje */}
      <IAFeedbackPanel />
    </div>
  );
}
